import React, {Component, FC} from 'react';
import styled from 'styled-components';
import i18n, { translations } from 'i18n';

import { push, back } from 'redux-first-router';
import { connectDeep } from 'redux-utils';

import App from 'models/app';

import {
  setFocusedClock,
  setTilesSynced
} from 'actions/gallery';

import { RoutePaths } from '../../constants';
import { DEVICE_RENAME as RENAME } from 'config';

import HomeContainer from 'components/v2/HomeContainer';
import { Portal } from '../../components/v2/Portal/index';
import ClocksRow from 'components/v2/MyApps/ClocksRow';
import AppsList from 'components/v2/MyApps/AppsList';
import SectionHeader from 'components/v2/MyApps/SectionHeader';
import Device from 'components/v2/MyApps/Device';
import ClockDetails from 'components/v2/MyApps/ClockDetails';
import { Transition } from 'components/v2/Transition';
import { sortFavoriteClockfaces } from '../../components/v2/MyApps/util';
import TilesRow from '../../components/v2/MyApps/TileRows';
import  { ScrollContainer } from '../../components/v2/ScrollContainer';
import { CustomizeSign, CustomizeButtonTextContainer, CustomizeButtonContainer, CustomizeButton, StyledLink } from '../../components/v2/Common';

import EditIcon from '../../icons/ic_edit.png';
import EditIconWhite from '../../icons/ic_edit_white.png';

type ReduxProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

interface MinePageProps extends ReduxProps {

}

const DEVICE_RENAME: any = RENAME;
const FULL_VH = document.documentElement.clientHeight;

const Container = styled.div`
  height: ${FULL_VH - 65}px;
  background: #FFFFFF;
`;

const mapStateToProps = (state: any) => {
  const { gallery, registry } = state;

  const {
    currentUser,
    installed: {
      loading: installedLoading,
      error: installedError,
      entries: installedEntries = [],
      tileStatuses: installedTiles
    },
    features: { flags, loading: featuresLoading },
    favorites: favoritesList
  } = registry;

  const {
    focusedClock,
    tilesSynced
  } = gallery;

  const installedClocks = [], installedApps = [];
  for (const { appId } of installedEntries) {
    const app = App.selectFromState(appId, state);
    if (app && app.isInstalledOrInstalling) {
      if (app.isClock) {
        installedClocks.push(app);
      } else {
        installedApps.push(app);
      }
    }
  }

  const favoriteClocks = [];
  for (const appId of favoritesList) {
    const app = App.selectFromState(appId, state);
    if (app && app.isClock) {
      favoriteClocks.push(app);
    }
  }

  let tiles = [];
  if (installedTiles && installedTiles.length > 0) {
    tiles = installedTiles.map((tile: any) => App.selectFromState(tile.tileId, state))
  }

  const appsLoading = installedLoading && installedEntries.length === 0;

  return {
    loading: appsLoading || featuresLoading,
    error: installedError,
    currentUser,
    focusedClock: focusedClock,
    installedApps,
    installedClocks,
    installedTiles: tiles,
    favoriteClocks,
    flags,
    tilesSynced
  }
};

const mapDispatchToProps = (dispatch: any) => ({
  setFocusedClock(clockId: string | null) {
    dispatch(setFocusedClock(clockId));
    clockId ? push('/v2/device?focused=true') : back();
  },
  setTilesSynced() {
    dispatch(setTilesSynced());
  },
});

const SettingsButton: FC<any> = (props) => {
  const {
    component: {
      AppDetails: { customizeClockfaceButtonText }
    }
  } = translations;

  return (
      <CustomizeButton onClick={() => props.app.navigateToSettings()} style={{"marginLeft" : "auto", "marginRight" : "auto", "marginBottom" : "23px"}}>
        <CustomizeButtonContainer>
          <CustomizeSign src={EditIconWhite} />
          <CustomizeButtonTextContainer>
            {customizeClockfaceButtonText()}
          </CustomizeButtonTextContainer>
        </CustomizeButtonContainer>
      </CustomizeButton>
  );
};

class MinePage extends Component<MinePageProps> {
  async componentDidMount() {
    // Used to capture when back button is pressed
    window.onhashchange = () => {
      const { focusedClock, setFocusedClock } = this.props;

      const { hash } = window.location;

      if (focusedClock && hash === '#/v2/device') {
        setFocusedClock(null);
      }
    };

    // trigger a tiles sync the first time the page is mounted
    if (this.tilesEnabled && !this.props.tilesSynced) {
      let commsResponse, errMessage;

      try {
        commsResponse = await window.__webview_comms__.trigger(
          'tiles-refresh'
        );
      } catch (err: any) {
        errMessage = err.message;
      }

      if (commsResponse && commsResponse.error) {
        errMessage = commsResponse.error;
      }

      if (errMessage) {
        console.error('Error triggering tiles refresh on app setup.');
      }
      this.props.setTilesSynced();
    }
  }

  componentWillUnmount() {
    window.onhashchange = null;
  }

  private get device() {
    const { currentUser: { device = {} } = {} } = this.props;

    return device;
  }

  private get deviceName() {
    const deviceName = this.device.productName.toLowerCase();
    return DEVICE_RENAME[deviceName] || deviceName;
  }

  private get favoriteClocksEnabled() {
    return (
      this.props.flags.enableFavoriteClockfaces && this.maxFavoriteClocks > 0
    );
  }

  private get tilesEnabled() {
    return this.props.flags.enable_tiles
  }

  private get maxFavoriteClocks() {
    return this.device.maxFavoriteClocks;
  }

  private get activeClock() {
    const { installedClocks } = this.props;

    if (installedClocks.length === 1) {
      return installedClocks[0];
    }

    const selectedClock = installedClocks.find(({ isSelected }) => isSelected);

    if (selectedClock) {
      return selectedClock;
    }

    return installedClocks.find(({ active }) => active) || {};
  }

  private get focusedClock() {
    const { installedClocks, favoriteClocks = [], focusedClock } = this.props;

    const allApps = [...installedClocks, ...favoriteClocks];

    return allApps.find(({ id }) => id === focusedClock);
  }

  private renderBody() {
    const firstClock = sortFavoriteClockfaces(this.props.favoriteClocks, this.activeClock)[0];

    return (
      <ScrollContainer
          navbarHeight={42}
          scrollKey={"minePage"}
      >
        <Container>
          <Device
            deviceName={this.deviceName}
            clock={this.activeClock}
            setFocusedActiveClock={() => this.props.setFocusedClock(this.activeClock.id)}
          />
          {this.activeClock.isClock && this.activeClock.isOnDevice && this.activeClock.hasSettings ? <SettingsButton app={this.activeClock}></SettingsButton> : null}
          <SectionHeader
            id='discover-more-clocks'
            aria-label='discover-more-clocks'
            titleText={this.favoriteClocksEnabled ?
              i18n('page.MyApps.myClocks')(): i18n('page.MyApps.myActiveClock')()}
          />
          <ClocksRow
            favoriteClocksEnabled={this.favoriteClocksEnabled}
            favoriteClocks={this.props.favoriteClocks}
            deviceName={this.deviceName}
            maxFavoriteClocks={this.maxFavoriteClocks}
            activeClock={this.activeClock}
            setFocusedClock={this.props.setFocusedClock}
          />
          { this.tilesEnabled && (
            <React.Fragment>
              <SectionHeader
                id='manage-tiles'
                aria-label='manage-tiles'
                titleText={i18n('page.MyApps.myTiles')()}
                linkText={i18n('page.MyApps.manage')()}
                link='/v2/my_tiles'
                icon={EditIcon}
              />
              <TilesRow
                favoriteTiles={this.props.installedTiles}
                deviceName={this.deviceName}
                maxFavoriteTiles={8}
                screenshot={firstClock?.banners[0]}
              />
            </React.Fragment>
          )}
          <SectionHeader
            id='discover-more-apps'
            aria-label='discover-more-apps'
            titleText={i18n('page.MyApps.myApps')()}
          />
          <AppsList apps={this.props.installedApps} />
          <Portal
            id='clock-details-screen'
            aria-label='clock-details-screen'
            content={
              <ClockDetails
                show={!!this.focusedClock}
                clock={this.focusedClock}
                deviceName={this.deviceName}
                favoriteClocksEnabled={this.favoriteClocksEnabled}
                favoriteClocksCount={this.props.favoriteClocks.length}
                installedClocksCount={this.props.installedClocks.length}
                maxFavoriteClocks={this.maxFavoriteClocks}
                setFocusedClock={this.props.setFocusedClock}
                isFirstClock={firstClock?.id === this.focusedClock?.id}
              />
            }
          />
        </Container>
       </ScrollContainer>
    );
  }

  render() {
    const { loading, error } = this.props;

    return (
      <HomeContainer selected={RoutePaths.DEVICE}>
        {
          loading || error ?
            <Transition loading={loading} error={error} headerHeight={42} /> :
              this.renderBody()
        }
      </HomeContainer>
    );
  }
}

export default connectDeep(mapStateToProps, mapDispatchToProps)(MinePage);
